import React, { ComponentPropsWithoutRef } from 'react';
import {
  CircularProgress,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertTitle,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Chip,
  Divider,
  Grid,
  KeyboardInput,
  Stepper,
  StepLabel,
  Step,
  StepButton,
  StepConnector,
  StepContent,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Banner,
  StickyFooter,
} from '@rbx/ui';

import SwaggerDocs from '@modules/ui/markdown/components/SwaggerDocs';
import BaseAccordion from '../components/BaseAccordion';
import LuaCode from '../components/LuaCode';
import MarkdownAlert from '../components/MarkdownAlert';
import MarkdownAccordion from '../components/MarkdownAccordion';
import MarkdownButton from '../components/MarkdownButton';
import MarkdownInlineCode from '../components/MarkdownInlineCode';
import MarkdownTable from '../components/MarkdownTable';
import MarkdownTableHead from '../components/MarkdownTableHead';
import GridContainer from '../components/GridContainer';
import MarkdownLink from '../components/MarkdownLink';
import MarkdownCardMedia from '../components/MarkdownCardMedia';
import MarkdownTabItem from '../components/MarkdownTabItem';
import MarkdownTabs from '../components/MarkdownTabs';
import DownloadStudioButton from '../components/deprecated/DownloadStudioButton';
import SideBarLink from '../components/SideBarLink';
import ScopeTable from '../components/ScopeTable';
import HomePageCategoryListingCard from '../components/HomePageCategoryListingCard';
import HomePageNewsCard from '../components/HomePageNewsCard';
import ExpertSection from '../components/ExpertSection';
import HomePageTopicCard from '../components/HomePageTopicCard';
import BrowseSampleCard from '../components/BrowseSampleCard';
import ScriptingLibrariesCard from '../components/ScriptingLibrariesCard';
import ColorSwatch from '../components/ColorSwatch';
import UseStudioButton from '../../../useStudio/components/UseStudioButton';
import MarkdownTranslation from '../components/MarkdownTranslation';
import { CustomComponentNames, CustomMDXComponentName } from './customComponentAliases';

/**
 * Note: those inline arrow function wrapper is necessary because the
 * `components` props expect something that returns a JSX.Element, which means
 * it cannot return `null`/`undefined` - this doesn't overlap with the provided
 * React component type definition, which allows those values
 */

// Custom markdown components for MDX
const customComponents = {
  Accordion: (props: ComponentPropsWithoutRef<typeof MarkdownAccordion>) => (
    <MarkdownAccordion {...props} />
  ),
  BaseAccordion: (props: ComponentPropsWithoutRef<typeof Accordion>) => (
    <BaseAccordion {...props} />
  ),
  AccordionDetails: (props: ComponentPropsWithoutRef<typeof AccordionDetails>) => (
    <AccordionDetails {...props} />
  ),
  AccordionSummary: (props: ComponentPropsWithoutRef<typeof AccordionSummary>) => (
    <AccordionSummary {...props} />
  ),
  Alert: MarkdownAlert,
  AlertTitle: (props: ComponentPropsWithoutRef<typeof AlertTitle>) => <AlertTitle {...props} />,
  Banner: (props: ComponentPropsWithoutRef<typeof Banner>) => <Banner {...props} />,
  Button: MarkdownButton,
  ButtonGroup: (props: ComponentPropsWithoutRef<typeof ButtonGroup>) => <ButtonGroup {...props} />,
  Card: (props: ComponentPropsWithoutRef<typeof Card>) => <Card {...props} />,
  CardActions: (props: ComponentPropsWithoutRef<typeof CardActions>) => <CardActions {...props} />,
  CardContent: (props: ComponentPropsWithoutRef<typeof CardContent>) => <CardContent {...props} />,
  CardHeader: (props: ComponentPropsWithoutRef<typeof CardHeader>) => <CardHeader {...props} />,
  CardMedia: MarkdownCardMedia,
  Chip: (props: ComponentPropsWithoutRef<typeof Chip>) => <Chip {...props} />,
  ColorSwatch: (props: ComponentPropsWithoutRef<typeof ColorSwatch>) => <ColorSwatch {...props} />,
  Container: (props: ComponentPropsWithoutRef<typeof Container>) => <Container {...props} />,
  Divider: (props: ComponentPropsWithoutRef<typeof Divider>) => <Divider {...props} />,
  Grid: (props: ComponentPropsWithoutRef<typeof Grid>) => <Grid {...props} />,
  InlineCode: MarkdownInlineCode,
  Link: MarkdownLink,
  KeyboardInput: (props: ComponentPropsWithoutRef<typeof KeyboardInput>) => (
    <KeyboardInput {...props} />
  ),
  Stepper: (props: ComponentPropsWithoutRef<typeof Stepper>) => <Stepper {...props} />,
  StepLabel: (props: ComponentPropsWithoutRef<typeof StepLabel>) => <StepLabel {...props} />,
  Step: (props: ComponentPropsWithoutRef<typeof Step>) => <Step {...props} />,
  StepButton: (props: ComponentPropsWithoutRef<typeof StepButton>) => <StepButton {...props} />,
  StepConnector: (props: ComponentPropsWithoutRef<typeof StepConnector>) => (
    <StepConnector {...props} />
  ),
  StepContent: (props: ComponentPropsWithoutRef<typeof StepContent>) => <StepContent {...props} />,
  Translation: (props: ComponentPropsWithoutRef<typeof MarkdownTranslation>) => (
    <MarkdownTranslation {...props} />
  ),
  Typography: (props: ComponentPropsWithoutRef<typeof Typography>) => <Typography {...props} />,
  Loader: (props: ComponentPropsWithoutRef<typeof CircularProgress>) => (
    <CircularProgress {...props} />
  ),
  TabItem: (props: ComponentPropsWithoutRef<typeof MarkdownTabItem>) => (
    <MarkdownTabItem {...props} />
  ),
  Tabs: (props: ComponentPropsWithoutRef<typeof MarkdownTabs>) => <MarkdownTabs {...props} />,
  Table: MarkdownTable,
  TableBody: (props: ComponentPropsWithoutRef<typeof TableBody>) => <TableBody {...props} />,
  TableCell: (props: ComponentPropsWithoutRef<typeof TableCell>) => <TableCell {...props} />,
  TableFooter: (props: ComponentPropsWithoutRef<typeof TableFooter>) => <TableFooter {...props} />,
  TableHead: MarkdownTableHead,
  TablePagination: (props: ComponentPropsWithoutRef<typeof TablePagination>) => (
    <TablePagination {...props} />
  ),
  TableRow: (props: ComponentPropsWithoutRef<typeof TableRow>) => <TableRow {...props} />,
  TableSortLabel: (props: ComponentPropsWithoutRef<typeof TableSortLabel>) => (
    <TableSortLabel {...props} />
  ),
  Tooltip: (props: ComponentPropsWithoutRef<typeof Tooltip>) => <Tooltip {...props} />,
  GridContainer,
  ScopeTable: (props: ComponentPropsWithoutRef<typeof ScopeTable>) => <ScopeTable {...props} />,
  SwaggerDocs: (props: ComponentPropsWithoutRef<typeof SwaggerDocs>) => <SwaggerDocs {...props} />,
  SideBarLink,
  HomePageNewsCard,
  HomePageTopicCard,
  HomePageCategoryListingCard,
  DownloadStudioButton: (props: ComponentPropsWithoutRef<typeof DownloadStudioButton>) => (
    <DownloadStudioButton {...props} />
  ),
  LuaCode,
  BrowseSampleCard,
  ScriptingLibrariesCard,
  StickyFooter: (props: ComponentPropsWithoutRef<typeof StickyFooter>) => (
    <StickyFooter {...props} />
  ),
  ExpertSection: (props: ComponentPropsWithoutRef<typeof ExpertSection>) => (
    <ExpertSection {...props} />
  ),
  UseStudioButton,
};

// must match keys in customComponentAliases.ts
if (
  Object.keys(customComponents).some(
    (k) => !CustomComponentNames.includes(k as CustomMDXComponentName),
  )
) {
  throw new Error(
    `Custom component keys must match the keys in customComponentAliases.ts: ${CustomComponentNames}`,
  );
}

export default customComponents;
